import { useState, useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { apiGetMyOwnedCards } from '../../api/userCardApi';
import type { Card, CardRarity } from '../../types/models/card';
import { useAppSelector } from '../../store/hooks';
import CardDetailModal from './CardDetailModal';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const RARITIES: CardRarity[] = ['common', 'rare', 'epic', 'legendary'];

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1600px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
  flex: 1;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 7px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  width: 220px;
  &:focus { outline: none; border-color: gold; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const RaritySelect = styled.select`
  padding: 7px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  &:focus { outline: none; border-color: gold; }
  option { background-color: #1a1a1a; }
`;

const ToggleOwnedBtn = styled.button<{ $active: boolean }>`
  padding: 7px 14px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.15)')};
  background-color: ${({ $active }) => ($active ? 'rgba(255,215,0,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.5)')};
  font-size: 13px;
  cursor: pointer;
`;

const CountText = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  margin-left: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
`;

const CardItem = styled.div<{ $owned: boolean }>`
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background-color: #222;
  transition: transform 0.12s, border-color 0.12s;
  filter: ${({ $owned }) => ($owned ? 'none' : 'grayscale(100%) opacity(0.45)')};

  &:hover {
    transform: scale(1.03);
    border-color: ${({ $owned }) => ($owned ? 'rgba(255,215,0,0.6)' : 'rgba(255,255,255,0.25)')};
    filter: ${({ $owned }) => ($owned ? 'none' : 'grayscale(60%) opacity(0.7)')};
  }
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 11px;
`;

const CardFooter = styled.div`
  padding: 5px 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  background-color: #1a1a1a;
`;

const CardIdLabel = styled.span`
  font-size: 11px;
  color: rgba(255,255,255,0.35);
`;

const RarityDot = styled.span<{ $rarity: CardRarity }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $rarity }) => RARITY_COLOR[$rarity]};
  flex-shrink: 0;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 48px;
  font-size: 15px;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  background-color: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.15);
  border-radius: 8px;
  padding: 14px 20px;
  margin-bottom: 24px;
`;

const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #fff;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ProgressTrack = styled.div`
  height: 5px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: gold;
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const ProgressLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
`;

const RarityRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const RarityChip = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CardsListPage: React.FC = () => {
  const loaderData = useLoaderData() as Card[] | null;
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );

  const [cards] = useState<Card[]>(loaderData ?? []);
  const [ownedMap, setOwnedMap] = useState<Map<number, number>>(new Map());
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [search, setSearch] = useState('');
  const [rarityFilter, setRarityFilter] = useState<'all' | CardRarity>('all');
  const [expansionFilter, setExpansionFilter] = useState<number | 'all'>('all');
  const [onlyOwned, setOnlyOwned] = useState(isAuthenticated);

  const expansionOptions = useMemo(() => {
    const map = new Map<number, string>();
    cards.forEach((card) => card.expansions.forEach((exp) => map.set(exp.id, exp.name)));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cards]);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyOwnedCards()
      .then((data) => {
        const map = new Map<number, number>();
        const list = Array.isArray(data) ? data : (data as any).data ?? [];
        list.forEach((e: { card_id: number; quantity: number }) => map.set(e.card_id, e.quantity));
        setOwnedMap(map);
      })
      .catch(() => {/* silenzioso: ownership non critica */});
  }, [isAuthenticated]);

  const collectionStats = useMemo(() => {
    if (!isAuthenticated || cards.length === 0) return null;
    const uniqueOwned = ownedMap.size;
    const totalCopies = Array.from(ownedMap.values()).reduce((s, q) => s + q, 0);
    const pct = Math.round((uniqueOwned / cards.length) * 100);
    const byRarity = (['common', 'rare', 'epic', 'legendary'] as CardRarity[]).map((r) => {
      const total = cards.filter((c) => c.rarity === r).length;
      const owned = cards.filter((c) => c.rarity === r && ownedMap.has(c.id)).length;
      return { rarity: r, owned, total };
    });
    return { uniqueOwned, totalCopies, pct, byRarity };
  }, [cards, ownedMap, isAuthenticated]);

  const filtered = useMemo(() => {
    return cards.filter((card) => {
      if (rarityFilter !== 'all' && card.rarity !== rarityFilter) return false;
      if (expansionFilter !== 'all' && !card.expansions.some((e) => e.id === expansionFilter)) return false;
      if (onlyOwned && !ownedMap.has(card.id)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          card.wrestlers.some((w) => w.name.toLowerCase().includes(q)) ||
          card.tags.some((t) => t.name.toLowerCase().includes(q)) ||
          card.ppvs.some((p) => (p.ppv_description?.name ?? '').toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [cards, rarityFilter, expansionFilter, onlyOwned, search, ownedMap]);

  return (
    <>
      <CommonWrapper>
        <PageInner>
          <PageHeader>
            <PageTitle>Lista Carte</PageTitle>
          </PageHeader>

          {collectionStats && (
            <StatsBar>
              <StatBlock>
                <StatLabel>Carte possedute</StatLabel>
                <StatValue>{collectionStats.uniqueOwned} / {cards.length}</StatValue>
              </StatBlock>

              <StatDivider />

              <StatBlock>
                <StatLabel>Copie totali</StatLabel>
                <StatValue>{collectionStats.totalCopies}</StatValue>
              </StatBlock>

              <StatDivider />

              <ProgressBarWrapper>
                <ProgressLabel>Completamento collezione — {collectionStats.pct}%</ProgressLabel>
                <ProgressTrack>
                  <ProgressFill $pct={collectionStats.pct} />
                </ProgressTrack>
              </ProgressBarWrapper>

              <StatDivider />

              <RarityRow>
                {collectionStats.byRarity.map(({ rarity, owned, total }) => (
                  <RarityChip key={rarity} $color={RARITY_COLOR[rarity]}>
                    {owned}/{total}
                  </RarityChip>
                ))}
              </RarityRow>
            </StatsBar>
          )}

          <FiltersRow>
            <SearchInput
              type="text"
              placeholder="Cerca per wrestler, tag, PPV..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <RaritySelect
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value as 'all' | CardRarity)}
            >
              <option value="all">Tutte le rarità</option>
              {RARITIES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </RaritySelect>
            <RaritySelect
              value={expansionFilter}
              onChange={(e) => setExpansionFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            >
              <option value="all">Tutte le espansioni</option>
              {expansionOptions.map((exp) => (
                <option key={exp.id} value={exp.id}>{exp.name}</option>
              ))}
            </RaritySelect>
            {isAuthenticated && (
              <ToggleOwnedBtn
                $active={onlyOwned}
                onClick={() => setOnlyOwned((v) => !v)}
              >
                Solo le mie
              </ToggleOwnedBtn>
            )}
            <CountText>{filtered.length} carte</CountText>
          </FiltersRow>

          {cards.length === 0 && (
            <StatusText>Nessuna carta disponibile.</StatusText>
          )}

          <Grid>
            {filtered.map((card) => {
              const src = toImgSrc(card.fronte_path);
              const owned = ownedMap.has(card.id);
              return (
                <CardItem key={card.id} $owned={owned} onClick={() => setSelectedCard(card)}>
                  <CardImgWrapper>
                    {src
                      ? <CardImg src={src} alt={`Carta #${card.id}`} />
                      : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                    }
                  </CardImgWrapper>
                  <CardFooter>
                    <CardIdLabel>#{card.id}</CardIdLabel>
                    <RarityDot $rarity={card.rarity} />
                  </CardFooter>
                </CardItem>
              );
            })}
          </Grid>
        </PageInner>
      </CommonWrapper>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          ownedQuantity={ownedMap.get(selectedCard.id) ?? 0}
          isAuthenticated={isAuthenticated}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </>
  );
};

export default CardsListPage;
